/**
 * What the backend refused, against the fields that carry it.
 *
 * The other half of `validate.ts`. That one evaluates the rules the definition states and is a
 * pure function of the form's values; this one reads an answer the server already gave, which is
 * an event. Together they are what `DeviceConfigForm` merges into one `errors` map.
 *
 * Not every rule is in the definition, by design: which zones a resident may be restricted to is
 * backend-api's to know, since the configuration service does not know what a zone is. For those,
 * the server's answer is the only explanation a user can be given — which is what makes placing it
 * on the right control a prerequisite rather than a nicety.
 */

import { Schema, collectAllFields } from '../schema';
import { configKeysForField } from './configMappers';
import { getFieldError, hasFieldErrors } from '~utils/fieldErrors';

export interface ServerErrors {
  /** Keyed by field id, ready to hand to the renderer. */
  byField: Record<string, string>;
  /**
   * The backend named fields and none of them are on this form.
   *
   * Worth knowing because a caller that hides its general message whenever fields were named would
   * otherwise fail in silence: nothing marked, nothing said. A parameter the catalog declares and
   * no layout places is the ordinary way this happens.
   */
  unplaced: boolean;
}

export const serverErrors = (schema: Schema | undefined, error?: Error): ServerErrors => {
  const byField: Record<string, string> = {};

  if (schema && error) {
    for (const field of collectAllFields(schema)) {
      // By configuration key, not field id: the backend names the key it refused, and a control
      // owning several is not named by any of them -- a zone rule comes back as
      // `indoorGeofencingZones` and belongs on the picker, whose own id is not a setting.
      const message = configKeysForField(field)
        .map((key) => getFieldError(key, error))
        .find(Boolean);
      if (message) byField[field.id] = message;
    }
  }

  return { byField, unplaced: hasFieldErrors(error) && Object.keys(byField).length === 0 };
};
